import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, writeBatch, limit, updateDoc, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getUserById, getFollowingList } from '../services/userService';

const ArtistAlerts = ({ embedded = false }) => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [followedPosts, setFollowedPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [markingAllRead, setMarkingAllRead] = useState(false);

    // Check if user is an artist
    useEffect(() => {
        (async () => {
            if (!auth.currentUser) {
                if (!embedded) navigate('/login');
                return;
            }
            try {
                const userData = await getUserById(auth.currentUser.uid);
                setUser(userData);
                if (userData?.role !== 'artist') {
                    if (!embedded) navigate('/');
                }
            } catch (err) {
                console.error('Error fetching user:', err);
                if (!embedded) navigate('/');
            }
        })();
        return () => {};
    }, [navigate, embedded]);

    // Subscribe to notifications in realtime
    useEffect(() => {
        if (!auth.currentUser || !user || user.role !== 'artist') {
            setLoading(false);
            return () => {};
        }

        setLoading(true);
        const notificationsRef = collection(db, 'notifications');
        const q = query(
            notificationsRef,
            where('userId', '==', auth.currentUser.uid),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const notifs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                // Sort by createdAt (fallback if orderBy doesn't work)
                notifs.sort((a, b) => {
                    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
                    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
                    return bTime - aTime;
                });
                setNotifications(notifs);
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching notifications:', error);
                if (error.code === 'failed-precondition') {
                    // Keep alerts usable while the optional composite index is unavailable.
                    getDocs(query(
                        notificationsRef,
                        where('userId', '==', auth.currentUser.uid)
                    )).then((fallbackSnapshot) => {
                        const fallbackNotifications = fallbackSnapshot.docs.map((notificationDoc) => ({
                            id: notificationDoc.id,
                            ...notificationDoc.data(),
                        }));
                        fallbackNotifications.sort((a, b) => {
                            const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                            const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                            return bTime - aTime;
                        });
                        setNotifications(fallbackNotifications);
                    }).catch((fallbackError) => {
                        console.error('Fallback notifications query failed:', fallbackError);
                    });
                }
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Load posts from users the artist follows
    useEffect(() => {
        if (!auth.currentUser || !user || user.role !== 'artist') {
            setFollowedPosts([]);
            return () => {};
        }

        let isMounted = true;
        const unsubs = [];
        
        (async () => {
            try {
                const followingIds = await getFollowingList(auth.currentUser.uid);
                
                if (!isMounted) return;
                
                if (!followingIds || followingIds.length === 0) {
                    setFollowedPosts([]);
                    return;
                }

                // Get posts from followed users (chunk if more than 10)
                const chunks = [];
                for (let i = 0; i < followingIds.length; i += 10) {
                    chunks.push(followingIds.slice(i, i + 10));
                }

                chunks.forEach((chunk) => {
                    if (!isMounted) return;
                    try {
                        const q = query(
                            collection(db, 'songs'),
                            where('artistId', 'in', chunk),
                            orderBy('createdAt', 'desc'),
                            limit(5)
                        );
                        const unsub = onSnapshot(
                            q,
                            (snapshot) => {
                                if (!isMounted) return;
                                const posts = snapshot.docs.map(doc => ({
                                    id: doc.id,
                                    ...doc.data(),
                                    type: 'post'
                                }));
                                setFollowedPosts(prev => {
                                    // Merge with previous posts, remove duplicates
                                    const merged = [...prev.filter(p => !chunk.includes(p.artistId)), ...posts];
                                    // Sort by createdAt desc
                                    merged.sort((a, b) => {
                                        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
                                        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
                                        return bTime - aTime;
                                    });
                                    return merged.slice(0, 20); // Limit to 20 recent posts
                                });
                            },
                            (err) => {
                                console.error('Error fetching followed posts:', err);
                            }
                        );
                        unsubs.push(unsub);
                    } catch (err) {
                        console.error('Error setting up followed posts query:', err);
                    }
                });
            } catch (err) {
                console.error('Error getting following list:', err);
                if (isMounted) setFollowedPosts([]);
            }
        })();

        return () => {
            isMounted = false;
            unsubs.forEach(unsub => {
                try {
                    unsub();
                } catch (e) {
                    // Ignore cleanup errors
                }
            });
        };
    }, [user]);

    const handleMarkAllRead = async () => {
        if (!auth.currentUser || notifications.length === 0) return;

        setMarkingAllRead(true);
        try {
            const unreadNotifications = notifications.filter(n => !n.read);
            if (unreadNotifications.length === 0) {
                setMarkingAllRead(false);
                return;
            }

            const batch = writeBatch(db);
            unreadNotifications.forEach(notif => {
                const notifRef = doc(db, 'notifications', notif.id);
                batch.update(notifRef, { read: true });
            });

            await batch.commit();
            // Reset user's unreadNotifications counter
            try {
                await updateDoc(doc(db, 'users', auth.currentUser.uid), { unreadNotifications: 0 });
            } catch {}
        } catch (error) {
            console.error('Error marking all as read:', error);
            alert('Failed to mark all as read. Please try again.');
        } finally {
            setMarkingAllRead(false);
        }
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p>Loading alerts...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${embedded ? '' : 'min-h-screen'} bg-[#0e0e0e] text-white`}>
            <div className="max-w-2xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        {!embedded && (
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 hover:bg-[#1a1a1a] rounded-lg transition"
                            >
                                ← Back
                            </button>
                        )}
                        <h1 className="text-2xl font-bold">Alerts</h1>
                        {unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                                {unreadCount} new
                            </span>
                        )}
                    </div>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            disabled={markingAllRead}
                            className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#222] rounded-lg transition text-sm font-medium disabled:opacity-50"
                        >
                            {markingAllRead ? 'Marking...' : 'Mark all read'}
                        </button>
                    )}
                </div>

                {/* Combined Feed: Notifications + Followed Posts */}
                {notifications.length === 0 && followedPosts.length === 0 ? (
                    <div className="text-center py-12 text-white/60">
                        <div className="text-4xl mb-4">🔔</div>
                        <p className="text-lg">No alerts yet</p>
                        <p className="text-sm mt-2">You'll see notifications here when someone follows you, sends a tip, or posts new content!</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Combine notifications and posts, sort by time */}
                        {[...notifications.map(n => ({ ...n, itemType: 'notification' })), ...followedPosts.map(p => ({ ...p, itemType: 'post' }))]
                            .sort((a, b) => {
                                const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.uploadDate?.toDate ? a.uploadDate.toDate().getTime() : 0));
                                const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.uploadDate?.toDate ? b.uploadDate.toDate().getTime() : 0));
                                return bTime - aTime;
                            })
                            .map((item) => {
                                if (item.itemType === 'notification') {
                                    const isUnread = !item.read;
                                    const createdAt = item.createdAt?.toDate ? item.createdAt.toDate() : new Date(item.createdAt?.seconds * 1000 || Date.now());
                                    const timeAgo = getTimeAgo(createdAt);

                                    return (
                                        <div
                                            key={item.id}
                                            className={`rounded-xl p-4 shadow-md transition-all cursor-pointer ${
                                                isUnread
                                                    ? 'bg-[#1a1a1a] border border-white/10 hover:bg-[#222]'
                                                    : 'bg-[#1a1a1a]/50 hover:bg-[#1a1a1a] opacity-75'
                                            }`}
                                            onClick={() => {
                                                // Navigate to sender's profile if available
                                                if (item.senderId) {
                                                    navigate(`/profile/${item.senderId}`);
                                                }
                                            }}
                                        >
                                            <div className="flex items-start gap-3">
                                                {/* Icon based on type */}
                                                <div className="flex-shrink-0 mt-1">
                                                    {item.type === 'follow' && (
                                                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                                                            <span className="text-xl">👤</span>
                                                        </div>
                                                    )}
                                                    {['tip', 'tip_confirmed', 'tip_pending', 'tip_failed'].includes(item.type) && (
                                                        <div
                                                            className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                                item.type === 'tip_failed'
                                                                    ? 'bg-red-500/20'
                                                                    : item.type === 'tip_pending'
                                                                        ? 'bg-yellow-500/20'
                                                                        : 'bg-green-500/20'
                                                            }`}
                                                        >
                                                            <span className="text-xl">
                                                                {item.type === 'tip_failed'
                                                                    ? '⚠️'
                                                                    : item.type === 'tip_pending'
                                                                        ? '⏳'
                                                                        : '💰'}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {item.type === 'mention' && (
                                                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                                            <span className="text-xl">💬</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-white ${isUnread ? 'font-medium' : 'font-normal'}`}>
                                                        {item.message}
                                                    </p>
                                                    <p className="text-xs text-white/50 mt-1">{timeAgo}</p>
                                                </div>

                                                {/* Unread indicator */}
                                                {isUnread && (
                                                    <div className="flex-shrink-0">
                                                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                } else {
                                    // Post from followed user
                                    const createdAt = item.createdAt?.toDate ? item.createdAt.toDate() : (item.uploadDate?.toDate ? item.uploadDate.toDate() : new Date());
                                    const timeAgo = getTimeAgo(createdAt);

                                    return (
                                        <div
                                            key={item.id}
                                            className="rounded-xl p-4 shadow-md transition-all cursor-pointer bg-[#1a1a1a] border border-white/10 hover:bg-[#222]"
                                            onClick={() => {
                                                // Navigate to artist's profile
                                                if (item.artistId) {
                                                    navigate(`/profile/${item.artistId}`);
                                                }
                                            }}
                                        >
                                            <div className="flex items-start gap-3">
                                                {/* Post icon */}
                                                <div className="flex-shrink-0 mt-1">
                                                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                                        <span className="text-xl">🎵</span>
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-white font-medium">
                                                        <span className="text-purple-400">@{item.artistName || item.userName || 'Artist'}</span> posted a new song
                                                    </p>
                                                    <p className="text-white/80 mt-1">{item.title || item.name || 'Untitled Song'}</p>
                                                    <p className="text-xs text-white/50 mt-1">{timeAgo}</p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                            })}
                    </div>
                )}
            </div>
        </div>
    );
};

// Helper function to get time ago
function getTimeAgo(date) {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return 'just now';
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks}w ago`;
    
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
}

export default ArtistAlerts;
